// src/components/ArchitectureFlow/ArchitectureFlow.tsx

import React from 'react';
import styles from './ArchitectureFlow.module.css';

const DATA_SOURCES = [
  { icon: '\uD83D\uDCCA', label: 'RSI Scanners' },
  { icon: '\uD83C\uDF0D', label: 'Geo / Oil' },
  { icon: '\uD83D\uDCF0', label: 'News' },
  { icon: '\uD83D\uDEA2', label: 'Shipping' },
];

const PIPELINE_STEPS = [
  { icon: '\uD83C\uDF0D', label: 'Regime', parallel: false },
  { icon: '\uD83D\uDD0D', label: 'Scanner', parallel: true },
  { icon: '\uD83D\uDCF0', label: 'Sentiment', parallel: true },
  { icon: '\uD83D\uDD17', label: 'Correlation', parallel: true },
  { icon: '\uD83E\uDDE0', label: 'Analyst', parallel: false },
  { icon: '\uD83D\uDCC5', label: 'Earnings', parallel: false },
  { icon: '\uD83D\uDEE1\uFE0F', label: 'Risk', parallel: false },
  { icon: '\u23F0', label: 'Timing', parallel: false },
];

const Connector: React.FC = () => (
  <div className={styles.connector}>
    <div className={styles.connectorLine} />
    <span className={styles.connectorArrow}>{'\u25BC'}</span>
  </div>
);

const ArchitectureFlow: React.FC = () => {
  // Build the pipeline inline display
  const renderPipeline = () => {
    const elements: React.ReactNode[] = [];

    // Regime (solo)
    const regime = PIPELINE_STEPS[0];
    elements.push(
      <div key="regime" className={styles.pipelineStep}>
        <span className={styles.pipelineStepIcon}>{regime.icon}</span>
        {regime.label}
      </div>,
    );
    elements.push(
      <span key="a1" className={styles.pipelineArrow}>
        {'\u25B6'}
      </span>,
    );

    // Parallel group
    const parallelSteps = PIPELINE_STEPS.filter((s) => s.parallel);
    elements.push(
      <div key="parallel" className={styles.pipelineParallel}>
        <span className={styles.parallelLabel}>{'\u2016'}</span>
        {parallelSteps.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className={styles.pipelineStep}>
              <span className={styles.pipelineStepIcon}>{s.icon}</span>
              {s.label}
            </div>
            {i < parallelSteps.length - 1 && <span className={styles.pipelineArrow}>+</span>}
          </React.Fragment>
        ))}
      </div>,
    );

    // Sequential steps after parallel
    const seqSteps = PIPELINE_STEPS.filter((s) => !s.parallel).slice(1);
    seqSteps.forEach((s) => {
      elements.push(
        <span key={`a-${s.label}`} className={styles.pipelineArrow}>
          {'\u25B6'}
        </span>,
      );
      elements.push(
        <div key={s.label} className={styles.pipelineStep}>
          <span className={styles.pipelineStepIcon}>{s.icon}</span>
          {s.label}
        </div>,
      );
    });

    return elements;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Strategist-First Architecture</span>
        <span className={styles.headerSub}>How SignalDeck works end-to-end</span>
      </div>

      <div className={styles.flow}>
        {/* ── 1. Data Sources ── */}
        <div className={`${styles.card} ${styles.dataSources} ${styles.delay0}`}>
          <div className={styles.cardTitle}>
            <span className={styles.cardIcon}>{'\uD83D\uDCE1'}</span>
            <span className={styles.cardLabel}>Data Sources</span>
          </div>
          <div className={styles.sourceGrid}>
            {DATA_SOURCES.map((s) => (
              <div key={s.label} className={styles.sourceTag}>
                <span>{s.icon}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <Connector />

        {/* ── 2. Bots (Scanners) ── */}
        <div className={`${styles.card} ${styles.bots} ${styles.delay1}`}>
          <div className={styles.cardTitle}>
            <span className={styles.cardIcon}>{'\uD83E\uDD16'}</span>
            <span className={styles.cardLabel}>Bots (Scanners)</span>
          </div>
          <p className={styles.cardDesc}>
            RSI signals detected <span className={styles.cardHighlight}>{'\u2192'} forwarded as BOT_SIGNAL</span>
          </p>
          <span className={styles.botMotto}>"Eyes on the market — detect, don't trade"</span>
        </div>

        <Connector />

        {/* ── 3. AI Strategist (Brain) ── */}
        <div className={`${styles.card} ${styles.strategist}`}>
          <div className={styles.cardTitle}>
            <span className={styles.cardIcon}>{'\uD83E\uDDE0'}</span>
            <span className={styles.cardLabel}>AI Strategist</span>
          </div>
          <p className={styles.cardDesc}>
            <span className={styles.cardHighlight}>12-Agent Pipeline</span> evaluates ALL signals and decides what to
            trade.
          </p>

          <div className={styles.pipelineInline}>{renderPipeline()}</div>

          <div className={styles.strategistOutputs}>
            <span className={styles.outputTag}>Entry</span>
            <span className={styles.outputTag}>Size</span>
            <span className={styles.outputTag}>Stop Loss</span>
            <span className={styles.outputTag}>Take Profit</span>
            <span className={styles.outputTag}>Timing</span>
          </div>
        </div>

        {/* ── Split connector ── */}
        <div className={styles.splitConnector}>
          <svg className={styles.splitConnectorSvg} viewBox="0 0 680 32" preserveAspectRatio="none">
            <path className={styles.splitPath} d="M 340 0 L 170 32" />
            <path className={styles.splitPath} d="M 340 0 L 510 32" />
          </svg>
        </div>

        {/* ── 4. Split row: Action Queue + Learning Loop ── */}
        <div className={styles.splitRow}>
          <div className={`${styles.card} ${styles.actionQueue} ${styles.delay3}`}>
            <div className={styles.cardTitle}>
              <span className={styles.cardIcon}>{'\uD83D\uDCCB'}</span>
              <span className={styles.cardLabel}>Action Queue</span>
            </div>
            <div className={styles.queueSteps}>
              <div className={styles.queueStep}>
                <span className={styles.queueDot} />
                Every trade logged here
              </div>
              <div className={styles.queueStep}>
                <span className={styles.queueDot} />
                Approve or Reject
              </div>
              <div className={styles.queueStep}>
                <span className={styles.queueDot} />
                Full audit trail
              </div>
            </div>
          </div>

          <div className={`${styles.card} ${styles.learningLoop} ${styles.delay3}`}>
            <div className={styles.cardTitle}>
              <span className={styles.cardIcon}>{'\uD83D\uDD04'}</span>
              <span className={styles.cardLabel}>Learning Loop</span>
            </div>
            <div className={styles.loopSteps}>
              <div className={styles.loopStep}>
                <span className={styles.loopDot} />
                Decision Journal {'\u2192'} Outcomes
              </div>
              <div className={styles.loopStep}>
                <span className={styles.loopDot} />
                Weekly Reviews {'\u2192'} Fed back
              </div>
              <div className={styles.loopStep}>
                <span className={styles.loopDot} />
                Strategy performance tracked
              </div>
            </div>
          </div>
        </div>

        {/* ── Merge connector ── */}
        <div className={styles.mergeConnector}>
          <svg className={styles.mergeConnectorSvg} viewBox="0 0 680 32" preserveAspectRatio="none">
            <path className={styles.mergePath} d="M 170 0 L 340 32" />
            <path className={styles.mergePath} d="M 510 0 L 340 32" />
          </svg>
        </div>

        {/* ── 5. Execution & Monitoring ── */}
        <div className={`${styles.card} ${styles.execution} ${styles.delay4}`}>
          <div className={styles.cardTitle}>
            <span className={styles.cardIcon}>{'\u26A1'}</span>
            <span className={styles.cardLabel}>Execution & Monitoring</span>
          </div>

          <div className={styles.execFlow}>
            <div className={`${styles.execStep} ${styles.execStepBreaker}`}>{'\uD83D\uDED1'} Circuit Breaker</div>
            <span className={styles.execArrow}>{'\u25B6'}</span>
            <div className={`${styles.execStep} ${styles.execStepValidate}`}>{'\u2705'} Order Validation</div>
            <span className={styles.execArrow}>{'\u25B6'}</span>
            <div className={`${styles.execStep} ${styles.execStepApi}`}>{'\uD83D\uDCE8'} Alpaca API</div>
          </div>

          <div className={styles.tpRow}>
            <span className={styles.tpLabel}>Bot Monitoring:</span>
            <span className={`${styles.tpTag} ${styles.tpTagGreen}`}>TP1 +3%</span>
            <span className={styles.tpSep}>{'\u25B6'}</span>
            <span className={`${styles.tpTag} ${styles.tpTagGreen}`}>TP2 +6%</span>
            <span className={styles.tpSep}>{'\u25B6'}</span>
            <span className={`${styles.tpTag} ${styles.tpTagGreen}`}>TP3 +9%</span>
            <span className={styles.tpSep}>|</span>
            <span className={`${styles.tpTag} ${styles.tpTagRed}`}>Stop Loss</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureFlow;
